// REACT
import React, {
	useContext,
	Dispatch,
	SetStateAction,
	useState,
	MutableRefObject,
	useRef,
	ChangeEvent,
	RefObject,
} from 'react'

// ROUTER
import { Link, useHistory } from 'react-router-dom'

// ESTILOS
import Styles from './Reservations.module.css'

// CONTEXTO
import MainContext from 'Context/MainContext'

// COMPONENTES
import DatePicker from 'Components/DatePicker/DatePicker'
import Location from 'Components/Location/Location'
import Input from 'Components/Input/Input'

// HERRAMIENTAS
import { saveReservationForm } from 'Utils/Querys'
import { verifyForm } from 'Utils/Tools'
import { showToast } from 'Utils/Fx'

// HOOKS
import { ScrollToTop } from 'Hooks/Hooks'

// ESTADO
interface ReservationState {
	verifyStates: boolean[]
	success: boolean
	firstForm: boolean
}

// FORMULARIO
interface ILocalFields extends IReservationFields {
	agreeWithPolicy: boolean
}

// DEFAULT FORM
const DefForm: ILocalFields = {
	from: null,
	to: null,
	date: new Date(),
	adults: 0,
	children: 0,
	name: '',
	email: '',
	phone: 0,
	room: 'Sencilla',
	pay: 'Efectivo',
	region: 'GT 502',
	state: 'Pre-reserva',
	id: '',
	agreeWithPolicy: false,
	work: false,
	parking: 'No',
}

// ESTADO POR DEFECTO
const DefState: ReservationState = {
	verifyStates: [true, false, false, false, false, true, true, true],
	success: false,
	firstForm: true,
}

const Reservations: React.FC = () => {
	// CONTEXTO
	const { reservation } = useContext(MainContext).lang

	const getDefValue = (index: number) => {
		// OBTENER OPCIONES
		const options: string[] | undefined = reservation.form.inputs[index].options

		// RETORNAR
		return options ? options[0] : ''
	}

	// VALORES POR DEFECTO
	const setDefValues = () => {
		if (window.localStorage) {
			DefForm.room = getDefValue(3)
			DefForm.pay = getDefValue(4)
			DefForm.parking = getDefValue(5)
			DefForm.name = window.localStorage.getItem('name') || ''
			DefForm.email = window.localStorage.getItem('email') || ''
			DefForm.phone = parseInt(window.localStorage.getItem('phone') || '0', 10)
		}
	}

	setDefValues()

	// VALORES POR DEFECTO CON LANG
	DefForm.room = getDefValue(3)
	DefForm.pay = getDefValue(4)
	DefForm.parking = getDefValue(5)

	// ESTADO
	const [formState, setData]: [
		ReservationState,
		Dispatch<SetStateAction<ReservationState>>
	] = useState(DefState)

	// ROUTER
	const history = useHistory()

	// REFERENCIAS
	const formRef: MutableRefObject<ILocalFields> = useRef({ ...DefForm })
	const policyCheckRef: RefObject<HTMLInputElement> = useRef(null)
	const workRef: RefObject<HTMLInputElement> = useRef(null)
	const sendBtn: RefObject<HTMLButtonElement> = useRef(null)

	// RECIBIR DATOS
	const getFormData = (value: string, name: string) => {
		// PHONE
		if (name === 'phone') formRef.current.phone = parseInt(value, 10)
		// @ts-ignore
		else formRef.current[name] = value

		// GUARDAR DATOS
		window.localStorage.setItem(name, value)
	}

	// GUARDAR DATOS EN LOCAL
	const saveFormData = (value: string, name: string) => {
		// PHONE
		if (name === 'phone') formRef.current.phone = parseInt(value, 10)
		// @ts-ignore
		else formRef.current[name] = value

		// ACTUALIZAR ESTADOS
		setData({ ...formState, verifyStates: verifyForm(formRef.current) })
	}

	// RECIBIR DATOS DE FECHAS
	const getDateData = (dateData: IDateFormData) => {
		// ASIGNAR DATOS DE PERSONAS
		formRef.current.from = dateData.date.from
		formRef.current.to = dateData.date.to
		formRef.current.adults = dateData.group.adults
		formRef.current.children = dateData.group.children
	}

	// ENVIAR DATOS
	const sendData = (ev: ChangeEvent<HTMLFormElement>) => {
		// EVITAR ENVIAR
		ev.preventDefault()

		// VERIFICAR FORMULARIO
		let success: boolean = true
		const verifyStates: boolean[] = verifyForm(formRef.current)

		// VERIFICAR TODOS LOS ESTADOS
		verifyStates.forEach((state: boolean) => {
			if (!state) success = false
		})

		// VERIFICAR QUE ACEPTA NUESTRAS POLITICAS
		if (!formRef.current.agreeWithPolicy) {
			// ERROR
			success = false

			// MOSTRAR ALERTA
			showToast({ text: reservation.toast.error })
		}

		if (success) {
			// ASIGNAR FECHA DE ENVIO
			formRef.current.date = new Date()

			// ASIGNAR REGION
			formRef.current.region = formRef.current.region.replace(' ', ' +')

			// BLOQUEAR
			if (sendBtn.current) sendBtn.current.classList.add(Styles.blockBtn)

			// MOSTRAR ALERTA DE ESPERA
			showToast({
				text: reservation.toast.send,
				fixed: true,
			})

			// ENVIAR DATOS
			saveReservationForm(formRef.current).then(() => {
				// HABILITAR
				if (sendBtn.current) sendBtn.current.classList.remove(Styles.blockBtn)

				// LIMPIAR NUEVOS DATOS
				const langCodeLocal: string = window.localStorage.getItem('lang') || 'ES'
				window.localStorage.clear()
				window.localStorage.setItem('lang', langCodeLocal)
				setDefValues()

				// ENVIAR ALERTA SATISFACTORIA
				showToast({
					text: reservation.toast.success,
					className: 'toastSuccess',
				})

				// ACTUALIZAR ESTADOS
				setData({ verifyStates, success, firstForm: false })

				// FORMULARIO DE ENVIO
				const body: any = { form: formRef.current }

				// CAMBIAR FECHAS
				body.form.from = body.form.from?.toString()
				body.form.to = body.form.to?.toString()

				// REINICIAR ESTADO
				fetch('https://pensive-brown-bc02a5.netlify.app/.netlify/functions/sendMail', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(body),
				}).then(() => setTimeout(() => setData(DefState), 3000))

				// REINICIAR  INPUTS Y REFERENCIAS
				if (policyCheckRef.current && workRef.current) {
					policyCheckRef.current.checked = false
					workRef.current.checked = false
				}
				formRef.current = { ...DefForm }
			})
		}
		// ACTUALIZAR ESTADOS
		else {
			// MOSTRAR ALERTA DE ERROR
			if (formRef.current.agreeWithPolicy)
				showToast({
					text: reservation.toast.error_1,
				})

			// ACTUALIZAR ESTADOS
			setData({ verifyStates, success, firstForm: false })
		}
	}

	// ACEPTAR POLITICAS
	const setPolicy = (ev: ChangeEvent<HTMLInputElement>) =>
		(formRef.current.agreeWithPolicy = ev.target.checked)

	// CHECK DE PARQUEO
	const setWork = (ev: ChangeEvent<HTMLInputElement>) => (formRef.current.work = ev.target.checked)

	// REGRESAR EN RUTA
	const goBack = () => history.goBack()

	// HOOK
	ScrollToTop(history.location.pathname)

	return (
		<section className={Styles.page}>
			<div className={Styles.header}>
				<div className={Styles.localNav}>
					<button className={Styles.backBtn} onClick={goBack}>
						<i className={`${Styles.backBtnIcon} lni lni-chevron-left`} />
					</button>
					<Location className={Styles.map} />
				</div>

				<h1 className={Styles.headerTitle}>{reservation.title}</h1>
				<p className={Styles.headerText}>{reservation.text}</p>
			</div>

			<div
				className={Styles.bottomSection}
				style={{ background: formState.success ? 'var(--yellow)' : 'hsl(214, 100%, 8%)' }}>
				<DatePicker
					color={formState.success ? 'rgba(255, 255, 255, 0.75)' : 'rgba(227, 165, 1, 0.75)'}
					className={`${Styles.pickers}${
						!formState.firstForm && (!formState.verifyStates[0] || !formState.verifyStates[0])
							? ' ' + Styles.errorPicker
							: ''
					}`}
					getData={getDateData}
					clearData={formState.success}
				/>

				<form className={Styles.form} onSubmit={sendData}>
					<h2 className={Styles.h2}>{reservation.form.title}</h2>
					{reservation.form.inputs.map((formField: IFormInput, key: number) => (
						<div className={Styles.formRow} key={key}>
							<label htmlFor={formField.field} className={Styles.label}>
								{formField.name}
							</label>
							<Input
								// @ts-ignore
								defValue={DefForm[formField.field]}
								index={key}
								firstRender={formState.firstForm}
								setError={formState.verifyStates[key + 2]}
								clearData={formState.success}
								options={formField.options}
								type={formField.type}
								name={formField.field}
								placeholder={formField.placeholder}
								getData={getFormData}
								onBlur={saveFormData}
							/>
						</div>
					))}

					<div className={Styles.optionalChecks}>
						<div>
							<input
								onChange={setWork}
								ref={workRef}
								type='checkbox'
								name='Work'
								className={Styles.policyCheck}
								id='work'
							/>
							<label htmlFor='work' className={Styles.checkLabel}>
								{reservation.form.workLabel}
							</label>
						</div>
					</div>

					<div className={Styles.policyCheckContainer}>
						<input
							type='checkbox'
							className={Styles.policyCheck}
							id='policyCheck'
							name='Policy'
							onChange={setPolicy}
							ref={policyCheckRef}
						/>
						<label htmlFor='policyCheck' className={Styles.checkLabel}>
							{reservation.form.checkLabel}
							<Link
								to='politicas'
								title='Políticas de reservación'
								className={Styles.checkLabelBtn}>
								{reservation.form.checkBtn}
							</Link>
						</label>
					</div>

					<button ref={sendBtn} className={`${Styles.sendBtn} btn`}>
						<i
							className={`lni lni-${formState.success ? 'checkmark-circle' : 'reply'} ${
								formState.success ? Styles.sendIcon : Styles.sendIconR
							}`}
						/>
						{formState.success ? reservation.form.successBtn : reservation.form.sendBtn}
					</button>

					<span style={{ opacity: formState.success ? '1' : '0' }} className={Styles.successMsg}>
						{reservation.form.successMessage}
					</span>
				</form>

				<Link to='/politicas' className={Styles.fixedPolicy} title='Políticas de reservación'>
					<i className={`${Styles.fixedPolicyIcon} lni lni-write`} />
					{reservation.form.checkBtn}
				</Link>
			</div>
		</section>
	)
}

export default Reservations
